import React from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Card,
  CardContent,
  Tabs,
  Tab,
  Chip,
  Divider,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { formatDate } from '../../utils/dateFormat';

const MarksView = ({ marks, selectedExamTab, onExamTabChange, onBack }) => {
  if (marks.length === 0) {
    return (
      <Box sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, pb: 1 }}>
          <IconButton onClick={onBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={700}>
            Marks
          </Typography>
        </Box>
        <Box sx={{ px: 2, textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No marks available yet
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 2 }}>
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Marks
        </Typography>
      </Box>

      <Box sx={{ px: 2 }}>
        <Tabs
          value={selectedExamTab}
          onChange={(e, newValue) => onExamTabChange(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {marks.map((exam, examIdx) => (
            <Tab
              key={exam.exam_id || examIdx}
              label={
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {exam.exam_name || 'Exam'}
                  </Typography>
                  {exam.exam_start_date && (
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(exam.exam_start_date)}
                    </Typography>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>

        {marks[selectedExamTab] && (
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {marks[selectedExamTab].exam_name || 'Exam'}
                  </Typography>
                  {marks[selectedExamTab].exam_start_date && (
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(marks[selectedExamTab].exam_start_date)}
                      {marks[selectedExamTab].exam_end_date &&
                        ` - ${formatDate(marks[selectedExamTab].exam_end_date)}`}
                    </Typography>
                  )}
                </Box>
                {marks[selectedExamTab].percentage !== undefined && (
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6" fontWeight={700} color="primary.main">
                      {marks[selectedExamTab].percentage}%
                    </Typography>
                    {marks[selectedExamTab].grade && (
                      <Chip
                        label={marks[selectedExamTab].grade}
                        size="small"
                        color={
                          ['A+', 'A', 'B'].includes(marks[selectedExamTab].grade)
                            ? 'success'
                            : marks[selectedExamTab].grade === 'F'
                            ? 'error'
                            : 'default'
                        }
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {marks[selectedExamTab].subjects && marks[selectedExamTab].subjects.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {marks[selectedExamTab].subjects.map((subject, subjIdx) => (
                    <Paper
                      key={subject.subject_id || subjIdx}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {subject.subject_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {subject.marks !== null && subject.marks !== undefined
                          ? `${subject.marks}/${subject.max_marks || 100}`
                          : 'Not yet marked'}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No subjects marked yet
                </Typography>
              )}

              {marks[selectedExamTab].total !== undefined && marks[selectedExamTab].max_total > 0 && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '2px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>
                      Total
                    </Typography>
                    <Typography variant="body1" fontWeight={700} color="primary.main">
                      {marks[selectedExamTab].total}/{marks[selectedExamTab].max_total}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default MarksView;

